import React, {FC} from 'react';

import collectOpen from 'assets/v3/guide/extension/collect-open.webp';
import collectSelect from 'assets/v3/guide/extension/collect-select.webp';
import collectTranslate1 from 'assets/v3/guide/extension/collect-translate-1.webp';
import collectTranslate2 from 'assets/v3/guide/extension/collect-translate-2.webp';
import devMode from 'assets/v3/guide/extension/dev-mode.webp';
import downloadImg from 'assets/v3/guide/extension/download.webp';
import listingCollect from 'assets/v3/guide/extension/listing-collect.webp';
import listingDownload from 'assets/v3/guide/extension/listing-download.webp';
import listingOpen from 'assets/v3/guide/extension/listing-open.webp';
import listingParse from 'assets/v3/guide/extension/listing-parse.webp';
import listingPreview from 'assets/v3/guide/extension/listing-preview.webp';
import listingSelect from 'assets/v3/guide/extension/listing-select.webp';
import listingSettings from 'assets/v3/guide/extension/listing-settings.webp';
import loadUnpacked from 'assets/v3/guide/extension/load-unpacked.webp';
import pinFloat from 'assets/v3/guide/extension/pin-float.webp';
import pinToolbar from 'assets/v3/guide/extension/pin-toolbar.webp';
import refine1 from 'assets/v3/guide/extension/refine-1.webp';
import refine2 from 'assets/v3/guide/extension/refine-2.webp';
import storeInstall from 'assets/v3/guide/extension/store-install.webp';
import unzipMac from 'assets/v3/guide/extension/unzip-mac.webp';
import unzipWin from 'assets/v3/guide/extension/unzip-win.webp';
import unzippedFiles from 'assets/v3/guide/extension/unzipped-files.webp';

/** 使用指南「浏览器插件」tab 英文正文（与 zh.tsx 元素结构 1:1） */
const EnContent: FC = () => {
  return (
    <>
      <h2>How to Install the Extension in Chrome and Other Browsers?</h2>

      <h3>Option 1: Install from the Chrome Web Store</h3>
      <p>
        To install from the official Chrome Web Store, first make sure your network can reach Google services (a proxy
        tool is required).
      </p>
      <ul>
        <li>
          Open the extension download link directly:
          <br />
          <a
            href="https://chromewebstore.google.com/detail/%E8%B1%A1%E5%AF%84%E5%9B%BE%E7%89%87%E7%BF%BB%E8%AF%91%E5%8A%A9%E6%89%8B/ejkpocbmomaefkinmkfbfnpjjfbinegb?utm_source=xj-doc"
            rel="noreferrer"
            target="_blank"
          >
            <span aria-hidden="true">🔗</span> Install from the Chrome Web Store
          </a>
        </li>
        <li>Click the “Add to Chrome” button in the top-right corner to finish installing.</li>
      </ul>
      <img alt="Install from the Chrome Web Store" decoding="async" loading="lazy" src={storeInstall} />

      <h3>Option 2: Download the Package and Install Manually</h3>
      <p>
        1. Click the zip package below to download the latest version of the extension:
        <br />
        <a href="https://i.tosoiot.com/assets/xj-chrome-ext.zip" rel="noreferrer" target="_blank">
          <span aria-hidden="true">📥</span> Download the browser extension
        </a>
      </p>
      <p>2. Once downloaded, unzip the file. It is a .zip file that extracts to a folder.</p>
      <p>macOS</p>
      <img alt="Unzipped folder on macOS" decoding="async" loading="lazy" src={unzipMac} />
      <p>Windows</p>
      <img alt="Unzipped folder on Windows" decoding="async" loading="lazy" src={unzipWin} />
      <p>The folder contains the following files:</p>
      <img alt="Contents of the extension folder" decoding="async" loading="lazy" src={unzippedFiles} />
      <p>3. Open the Chrome “Manage extensions” page.</p>
      <ul>
        <li>Method 1: type chrome://extensions/ in the address bar</li>
        <li>Method 2: click the browser menu in the top-right corner, open Settings and find Extensions</li>
      </ul>
      <p>Turn on “Developer mode” in the top-right corner.</p>
      <img alt="Turn on Developer mode" decoding="async" loading="lazy" src={devMode} />
      <p>
        4. Click “Load unpacked”. In the dialog, select the folder you just unzipped and confirm — the extension is now
        imported.
      </p>
      <img alt="Load unpacked extension" decoding="async" loading="lazy" src={loadUnpacked} />
      <p>
        After importing, pin the extension to the toolbar and enable the floating widget for quick access. Refresh the
        page and you are ready to collect images.
      </p>
      <img alt="Pin the extension to the toolbar" decoding="async" loading="lazy" src={pinToolbar} />
      <img alt="Floating widget" decoding="async" loading="lazy" src={pinFloat} />

      <h2>How to Collect and Translate Images with the Extension?</h2>

      <h3>1. Open the product page you want to translate</h3>
      <img alt="Open the product page to translate" decoding="async" loading="lazy" src={collectOpen} />

      <h3>2. Choose Image Translation and collect the product images</h3>
      <p>Click the extension icon or the floating widget, choose Image Translation, and collect the product images.</p>
      <img alt="Choose the translation type" decoding="async" loading="lazy" src={collectSelect} />
      <p>
        Select the images you want to translate, choose the target language and click Translate to start processing.
      </p>
      <img alt="Select product shots to translate" decoding="async" loading="lazy" src={collectTranslate1} />
      <img alt="Start translating the collection" decoding="async" loading="lazy" src={collectTranslate2} />

      <h3>3. Download or retouch the translated images</h3>
      <p>
        Click any image to enlarge it and check the translation. If you are satisfied, batch-download to your device.
      </p>
      <img alt="Preview and batch-download results" decoding="async" loading="lazy" src={downloadImg} />
      <p>Not satisfied? Click Retouch to jump to the Xiangji website for further editing.</p>
      <img alt="Click Retouch" decoding="async" loading="lazy" src={refine1} />
      <img alt="Retouch on the Xiangji website" decoding="async" loading="lazy" src={refine2} />

      <h2>How to Collect Product Images for Main & Detail Image Generation?</h2>

      <h3>1. Open the product page for which you want to generate detail images or main images</h3>
      <img alt="Open the product page for generation" decoding="async" loading="lazy" src={listingOpen} />

      <h3>2. Choose Detail Page Generation and collect the product images</h3>
      <p>
        Click the extension icon or the floating widget, choose Detail Page Generation, and collect the product images.
      </p>
      <img alt="Choose Detail Page Generation" decoding="async" loading="lazy" src={listingCollect} />
      <p>
        Select the images to use (product close-ups, white-background shots, angle shots and lifestyle shots), then
        click Detail Page Generation.
      </p>
      <img alt="Select product shots to use" decoding="async" loading="lazy" src={listingSelect} />
      <p>
        After you click Detail Page Generation, the product information and selected images are parsed automatically,
        and in a few seconds you are redirected to the Xiangji workbench.
      </p>
      <img
        alt="Parse product information and redirect to the workbench"
        decoding="async"
        loading="lazy"
        src={listingParse}
      />
      <p>Complete the generation settings according to your product needs.</p>
      <img alt="Complete the generation settings" decoding="async" loading="lazy" src={listingSettings} />
      <p>
        A preview set is generated within a very short time so you can confirm the style and effect. Not satisfied?
        Generate again. Satisfied? Click to generate the final high-resolution images.
      </p>
      <img alt="Preview set" decoding="async" loading="lazy" src={listingPreview} />

      <h3>3. Download</h3>
      <img alt="Download the generated results" decoding="async" loading="lazy" src={listingDownload} />
    </>
  );
};

export default EnContent;
